/**
 * Smart Validation Utilities for Knowledge Pack Forms
 * Includes: required/optional markers, date logic, numeric ranges, URL/email validation, relationship rules
 */

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// URL validation regex
const URL_REGEX = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

// Bilingual requirement mapping
export const BILINGUAL_REQUIRED = {
  product: true,      // Customer-facing
  promotion: true,    // Customer-facing
  direction: false,   // Internal strategy (SINGLE LANGUAGE)
  values: true,       // Brand consistency
  services: true,     // Customer-facing
  compliance: true,   // Legal safety (MUST be bilingual)
  viral: false,       // Creative patterns (single language core)
  trend: false,       // Internal intelligence (SINGLE LANGUAGE)
  ambassador: true,   // Speaks to both audiences
  health: true,       // Critical health info
};

/**
 * Validate email address
 */
export const validateEmail = (email) => {
  if (!email || !email.trim()) {
    return { valid: false, error: 'Email is required' };
  }
  if (!EMAIL_REGEX.test(email)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  return { valid: true };
};

/**
 * Validate URL
 */
export const validateURL = (url) => {
  if (!url || !url.trim()) {
    return { valid: false, error: 'URL is required' };
  }
  if (!URL_REGEX.test(url)) {
    return { valid: false, error: 'Please enter a valid URL' };
  }
  return { valid: true };
};

/**
 * Validate date range (start < end)
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate) {
    return { valid: false, error: 'Start date is required' };
  }
  if (!endDate) {
    return { valid: false, error: 'End date is required' };
  }
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime())) {
    return { valid: false, error: 'Invalid start date' };
  }
  if (isNaN(end.getTime())) {
    return { valid: false, error: 'Invalid end date' };
  }
  if (start >= end) {
    return { valid: false, error: 'Start date must be before end date' };
  }
  
  return { valid: true };
};

/**
 * Validate numeric range
 */
export const validateNumericRange = (value, min, max, label = 'Value') => {
  if (value === undefined || value === null || value === '') {
    return { valid: false, error: `${label} is required` };
  }
  
  const num = Number(value);
  
  if (isNaN(num)) {
    return { valid: false, error: `${label} must be a number` };
  }
  if (min !== undefined && num < min) {
    return { valid: false, error: `${label} must be at least ${min}` };
  }
  if (max !== undefined && num > max) {
    return { valid: false, error: `${label} must not exceed ${max}` };
  }
  
  return { valid: true };
};

/**
 * Validate array length
 */
export const validateArrayLength = (array, minLength, maxLength, label = 'Items') => {
  if (!Array.isArray(array)) {
    return { valid: false, error: `${label} must be an array` };
  }
  
  const length = array.length;
  
  if (minLength !== undefined && length < minLength) {
    return { valid: false, error: `Please add at least ${minLength} ${label.toLowerCase()}` };
  }
  if (maxLength !== undefined && length > maxLength) {
    return { valid: false, error: `Maximum ${maxLength} ${label.toLowerCase()} allowed` };
  }
  
  return { valid: true };
};

/**
 * Validate bilingual fields
 * UPDATED: Universal Language Input - accepts ANY language (EN, MS, or mixed)
 * AI adapts to audience language at generation time
 */
export const validateBilingualField = (bilingualObj, fieldName, packType, isRequired = true) => {
  const requiresBilingual = BILINGUAL_REQUIRED[packType];
  
  if (!isRequired && (!bilingualObj?.en?.trim() && !bilingualObj?.ms?.trim())) {
    return { valid: true }; // Optional and empty is OK
  }
  
  if (!bilingualObj) {
    return { valid: false, error: `${fieldName} is required` };
  }
  
  // UPDATED: Universal Language Input - at least ONE language required (EN or MS)
  const hasEnglish = bilingualObj.en && bilingualObj.en.trim();
  const hasMalay = bilingualObj.ms && bilingualObj.ms.trim();
  
  if (!hasEnglish && !hasMalay) {
    return { valid: false, error: `${fieldName} is required (type in any language - BM, EN, or mix)` };
  }
  
  // For bilingual pack types, strongly recommend both languages (but don't block)
  // The Universal Language Input feature means AI will adapt at generation time
  if (requiresBilingual && !hasEnglish && hasMalay) {
    // Only MS provided - this is OK with Universal Language Input
    // AI will translate/adapt as needed
  }
  
  if (requiresBilingual && hasEnglish && !hasMalay) {
    // Only EN provided - this is OK with Universal Language Input
    // AI will translate/adapt as needed
  }
  
  return { valid: true };
};

/**
 * Validate relationship requirements
 * E.g., Promotion must link to at least 1 Product
 */
export const validateRelationshipRules = (packType, relationships) => {
  const errors = {};
  
  switch (packType) {
    case 'promotion':
      // Promotion must link to at least one product
      if (!relationships.linked_packs || relationships.linked_packs.length === 0) {
        errors.linked_packs = 'Promotion must be linked to at least one Product pack';
      }
      break;
      
    case 'compliance':
      // Compliance packs should have safe alternatives defined
      if (relationships.linked_packs && relationships.linked_packs.length > 0) {
        // This is good - compliance is linked to other packs
      }
      break;
      
    // Add more relationship rules as needed
  }
  
  return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Validate Product pack content
 */
export const validateProductContent = (content, touched = {}) => {
  const errors = {};
  
  if (touched.features && (!content.features || content.features.length < 2)) {
    errors.features = 'Please add at least 2 features';
  }
  
  if (touched.benefits && (!content.benefits || content.benefits.length < 2)) {
    errors.benefits = 'Please add at least 2 benefits';
  }
  
  if (touched.pricing && (!content.pricing || !content.pricing.trim())) {
    errors.pricing = 'Pricing information is required';
  }
  
  if (touched.target_audience && (!content.target_audience || !content.target_audience.trim())) {
    errors.target_audience = 'Target audience is required';
  }
  
  return errors;
};

/**
 * Validate Promotion pack content
 */
export const validatePromotionContent = (content, touched = {}) => {
  const errors = {};
  
  if (touched.campaign_name && (!content.campaign_name || !content.campaign_name.trim())) {
    errors.campaign_name = 'Campaign name is required';
  }
  
  if (touched.offer_details && (!content.offer_details || !content.offer_details.trim())) {
    errors.offer_details = 'Offer details are required';
  }
  
  if (touched.start_date && touched.end_date) {
    const dateValidation = validateDateRange(content.start_date, content.end_date);
    if (!dateValidation.valid) {
      errors.date_range = dateValidation.error;
    }
  } else if (touched.start_date && !content.start_date) {
    errors.start_date = 'Start date is required';
  } else if (touched.end_date && !content.end_date) {
    errors.end_date = 'End date is required';
  }
  
  if (touched.terms && content.terms && content.terms.length < 1) {
    errors.terms = 'Please add at least 1 term & condition';
  }
  
  return errors;
};

/**
 * Validate Direction pack content
 */
export const validateDirectionContent = (content, touched = {}) => {
  const errors = {};
  
  if (touched.strategic_goals && (!content.strategic_goals || content.strategic_goals.length < 2)) {
    errors.strategic_goals = 'Please add at least 2 strategic goals';
  }
  
  if (touched.key_messages && (!content.key_messages || content.key_messages.length < 2)) {
    errors.key_messages = 'Please add at least 2 key messages';
  }
  
  if (touched.target_segments && (!content.target_segments || !content.target_segments.trim())) {
    errors.target_segments = 'Target segments are required';
  }
  
  return errors;
};

/**
 * Validate Values pack content
 */
export const validateValuesContent = (content, touched = {}) => {
  const errors = {};
  
  if (touched.core_principles && (!content.core_principles || content.core_principles.length < 2)) {
    errors.core_principles = 'Please add at least 2 core principles';
  }
  
  if (touched.behavioral_guidelines && (!content.behavioral_guidelines || content.behavioral_guidelines.length < 2)) {
    errors.behavioral_guidelines = 'Please add at least 2 behavioral guidelines';
  }
  
  return errors;
};

/**
 * Validate Compliance pack content
 */
export const validateComplianceContent = (content, touched = {}) => {
  const errors = {};
  
  if (touched.regulation_name && (!content.regulation_name || !content.regulation_name.trim())) {
    errors.regulation_name = 'Regulation name is required';
  }
  
  if (touched.prohibited_phrases && (!content.prohibited_phrases || content.prohibited_phrases.length < 1)) {
    errors.prohibited_phrases = 'Please add at least 1 prohibited phrase';
  }
  
  if (touched.safe_alternatives && (!content.safe_alternatives || Object.keys(content.safe_alternatives).length === 0)) {
    errors.safe_alternatives = 'Please provide safe alternatives for prohibited phrases';
  }
  
  return errors;
};

/**
 * Get content validator by pack type
 */
export const getContentValidator = (packType) => {
  const validators = {
    product: validateProductContent,
    promotion: validatePromotionContent,
    direction: validateDirectionContent,
    values: validateValuesContent,
    compliance: validateComplianceContent,
  };
  
  return validators[packType] || (() => ({}));
};

/**
 * Validate entire form
 */
export const validateKnowledgePackForm = (formData, touched = {}) => {
  const errors = {};
  
  // Validate name (bilingual check)
  const nameValidation = validateBilingualField(formData.name, 'Name', formData.pack_type, true);
  if (!nameValidation.valid) {
    errors.name = nameValidation.error;
  }
  
  // Validate description (bilingual check)
  const descriptionValidation = validateBilingualField(formData.description, 'Description', formData.pack_type, true);
  if (!descriptionValidation.valid) {
    errors.description = descriptionValidation.error;
  }
  
  // Validate content based on pack type
  const contentValidator = getContentValidator(formData.pack_type);
  const contentErrors = contentValidator(formData.content, touched);
  if (Object.keys(contentErrors).length > 0) {
    errors.content = contentErrors;
  }
  
  // Validate relationships
  const relationshipValidation = validateRelationshipRules(formData.pack_type, formData.relationships);
  if (!relationshipValidation.valid) {
    errors.relationships = relationshipValidation.errors;
  }
  
  return { valid: Object.keys(errors).length === 0, errors };
};
